import React, { useState } from 'react';
import { Search, Users, Shield, Star, Trash2, Edit2, CheckCircle, XCircle } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import UserDetailsModal from '../../components/admin/UserDetailsModal';
import DeleteUserModal from '../../components/admin/DeleteUserModal';
import EditUserModal from '../../components/admin/EditUserModal';
import { useUserStore } from '../../stores/userStore';
import { formatDate } from '../../utils/formatters';
import { User } from '../../types/user';

const AdminUsers = () => {
  const { users, removeUser, updateUser } = useUserStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: 'all',
    verified: 'all'
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  const showAlert = (message: string, type: 'success' | 'error' = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const filteredUsers = Object.values(users).filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.fullName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filters.role === 'all' || user.role === filters.role;
    const matchesVerified = filters.verified === 'all' || 
                           (filters.verified === 'verified' && user.verificationStatus === 'verified') ||
                           (filters.verified === 'unverified' && !user.verificationStatus);
    
    return matchesSearch && matchesRole && matchesVerified;
  });

  const handleDeleteUser = async (email: string) => {
    try {
      removeUser(email);
      showAlert('User deleted successfully');
      setUserToDelete(null);
    } catch (error) {
      showAlert('Failed to delete user', 'error');
    }
  };

  const handleUpdateUser = async (updatedUser: User) => {
    try {
      updateUser(updatedUser.email, updatedUser);
      showAlert('User updated successfully');
      setUserToEdit(null);
    } catch (error) {
      showAlert('Failed to update user', 'error');
    }
  };

  const toggleVerification = async (user: User, type: 'id' | 'address') => {
    const currentStatus = user.verifications?.[type]?.status;
    const newStatus = currentStatus === 'verified' ? 'unverified' : 'verified';
    
    const updatedUser = {
      ...user,
      verifications: {
        ...user.verifications,
        [type]: {
          ...user.verifications?.[type],
          status: newStatus,
          verifiedDate: newStatus === 'verified' ? new Date().toISOString() : undefined
        }
      }
    };

    try {
      updateUser(user.email, updatedUser);
      showAlert(`${type.toUpperCase()} verification ${newStatus}`);
    } catch (error) {
      showAlert(`Failed to update ${type} verification status`, 'error');
    }
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {alert && (
          <div className={`p-4 rounded-lg ${
            alert.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {alert.message}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <select
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Roles</option>
              <option value="renter">Renters</option>
              <option value="owner">Owners</option>
              <option value="admin">Admins</option>
            </select>
            <select
              value={filters.verified}
              onChange={(e) => setFilters({ ...filters, verified: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Verification</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="min-w-full divide-y divide-gray-200">
            <div className="bg-gray-50">
              <div className="grid grid-cols-12 gap-4 px-6 py-3">
                <div className="col-span-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verification
                </div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </div>
                <div className="col-span-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </div>
              </div>
            </div>

            <div className="divide-y divide-gray-200 bg-white">
              {filteredUsers.map((user) => (
                <div key={user.id} className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50">
                  <div className="col-span-4">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        {user.photo ? (
                          <img
                            src={user.photo}
                            alt={user.fullName}
                            className="h-10 w-10 rounded-full"
                          />
                        ) : (
                          <Users className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.fullName || 'Unnamed User'}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.role === 'admin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-indigo-100 text-indigo-800'
                    }`}>
                      {user.role}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center space-x-2">
                    {user.verifications?.id && (
                      <button
                        onClick={() => toggleVerification(user, 'id')}
                        className={`p-1 rounded-full ${
                          user.verifications.id.status === 'verified'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                        title="ID Verification"
                      >
                        <Shield className="h-4 w-4" />
                      </button>
                    )}
                    {user.verifications?.address && (
                      <button
                        onClick={() => toggleVerification(user, 'address')}
                        className={`p-1 rounded-full ${
                          user.verifications.address.status === 'verified'
                            ? 'bg-purple-100 text-purple-600'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                        title="Address Verification"
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="col-span-2 flex items-center text-sm text-gray-500">
                    {formatDate(user.joinDate || '')}
                  </div>
                  <div className="col-span-2 flex items-center justify-end space-x-2">
                    <button
                      onClick={() => setSelectedUser(user)}
                      className="p-1.5 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                      title="View Details"
                    >
                      <Users className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setUserToEdit(user)}
                      className="p-1.5 text-indigo-600 hover:text-indigo-900 rounded-full hover:bg-indigo-50 transition-colors"
                      title="Edit User"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    {user.role !== 'admin' && (
                      <button
                        onClick={() => setUserToDelete(user.email)}
                        className="p-1.5 text-red-600 hover:text-red-900 rounded-full hover:bg-red-50 transition-colors"
                        title="Delete User"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {filteredUsers.length === 0 && (
                <div className="px-6 py-8 text-center">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Try adjusting your search or filter criteria
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}

      {userToEdit && (
        <EditUserModal
          user={userToEdit}
          onSave={handleUpdateUser}
          onClose={() => setUserToEdit(null)}
        />
      )}

      {userToDelete && (
        <DeleteUserModal
          email={userToDelete}
          onConfirm={handleDeleteUser}
          onClose={() => setUserToDelete(null)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminUsers;